import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { 
  Users, 
  UserCheck, 
  FileText, 
  Settings, 
  UserPlus, 
  Crown, 
  Shield, 
  User, 
  Mail, 
  Calendar, 
  MapPin, 
  Building, 
  Star, 
  Clock, 
  CheckCircle, 
  XCircle, 
  AlertCircle,
  Briefcase,
  Eye,
  MessageSquare
} from 'lucide-react'
import { apiClient } from '@/lib/api'
import TeamSharedPositions from '@/components/teams/TeamSharedPositions'

interface TeamMember {
  id: number | string
  user_id?: number
  uuid?: string
  name: string
  email: string
  role: string
  joinedAt: string
  status: string
  permissions?: string[]
}

interface TeamCandidate {
  id: number
  name: string
  email: string
  position: string
  status: 'applied' | 'screening' | 'interview_scheduled' | 'interview_completed' | 'offer_extended' | 'hired' | 'rejected'
  interview_progress: string
  assigned_date: string
  assigned_by: string
}

interface TeamDetails {
  id: number
  uuid: string
  name: string
  description: string
  team_type: string
  max_members: number
  member_count: number
  is_active: boolean
  created_at: string
  team_lead: {
    id: number
    name: string
    email: string
  } | null
}

interface TeamDetailTabsProps {
  teamId: string
  team: TeamDetails
}

export default function TeamDetailTabs({ teamId, team }: TeamDetailTabsProps) {
  const [activeTab, setActiveTab] = useState<'members' | 'positions' | 'candidates'>('members')
  const [members, setMembers] = useState<TeamMember[]>([])
  const [candidates, setCandidates] = useState<TeamCandidate[]>([])
  const [loading, setLoading] = useState(false)
  const [actionMemberId, setActionMemberId] = useState<number | null>(null)
  const [currentUser, setCurrentUser] = useState<any>(null)

  useEffect(() => {
    const storedUser = localStorage.getItem('currentUser')
    if (storedUser) {
      try {
        setCurrentUser(JSON.parse(storedUser))
      } catch (err) {
        console.warn('Failed to parse currentUser from storage', err)
      }
    }
  }, [])

  useEffect(() => {
    if (activeTab === 'positions') return
    loadTabData()
  }, [activeTab, teamId])

  const loadTabData = async () => {
    setLoading(true)
    try {
      switch (activeTab) {
        case 'members':
          await loadMembers()
          break
        case 'candidates':
          await loadCandidates()
          break
      }
    } catch (error) {
      console.error(`Failed to load ${activeTab} data:`, error)
    } finally {
      setLoading(false)
    }
  }

  const loadMembers = async () => {
    try {
      const response = await apiClient.getTeamMembers(teamId)
      if (response.success && response.data?.members) {
        // Transform API response to match our interface
        const transformedMembers = response.data.members.map((member: any) => ({
          id: member.membership_id || member.id,
          user_id: member.user_id,
          uuid: member.user_uuid || member.uuid || member.id,
          name: member.user?.name || member.name,
          email: member.user?.email || member.email,
          role: member.team_role || member.role || 'member',
          joinedAt: member.joined_at || member.joinedAt,
          status: member.status || 'active',
          permissions: member.permissions || []
        }))
        setMembers(transformedMembers)
      } else {
        setMembers([])
      }
    } catch (error) {
      console.error('Error loading team members:', error)
      setMembers([])
    }
  }

  const memberMatchesCurrentUser = (member: TeamMember) => {
    if (!currentUser) return false

    const memberIds = [member.id, member.user_id, member.uuid]
    const userIds = [currentUser.id, currentUser.uuid, currentUser.user_id, currentUser.userId]

    const idMatch = memberIds.some(mId =>
      mId !== undefined &&
      userIds.some(uId => uId !== undefined && String(mId).toLowerCase() === String(uId).toLowerCase())
    )

    if (idMatch) return true

    if (member.email && currentUser.email) {
      return member.email.toLowerCase() === String(currentUser.email).toLowerCase()
    }
    return false
  }

  const loadCandidates = async () => {
    try {
      // Try to fetch candidates for this team
      const response = await apiClient.getCandidates({ team_id: teamId })
      if (response.success && response.data) {
        // Handle both array and paginated response formats
        const candidatesData = Array.isArray(response.data) ? response.data : response.data.candidates || []
        
        const transformedCandidates = candidatesData.map((candidate: any) => ({
          id: candidate.id,
          name: candidate.name,
          email: candidate.email,
          position: candidate.position_title || candidate.position || 'N/A',
          status: candidate.status || 'applied',
          interview_progress: candidate.interview_progress || 'N/A',
          assigned_date: candidate.assigned_date || candidate.created_at,
          assigned_by: candidate.assigned_by || 'N/A'
        }))
        setCandidates(transformedCandidates)
      } else {
        setCandidates([])
      }
    } catch (error) {
      console.error('Error loading candidates:', error)
      setCandidates([])
    }
  }

  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'lead': return <Crown className="w-4 h-4 text-yellow-600" />
      case 'admin': return <Shield className="w-4 h-4 text-blue-600" />
      default: return <User className="w-4 h-4 text-gray-500" />
    }
  }

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'lead': return 'bg-yellow-100 text-yellow-800'
      case 'admin': return 'bg-blue-100 text-blue-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800'
      case 'pending': return 'bg-yellow-100 text-yellow-800'
      case 'inactive': return 'bg-red-100 text-red-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const getCandidateStatusColor = (status: string) => {
    switch (status) {
      case 'applied': return 'bg-blue-100 text-blue-800'
      case 'screening': return 'bg-yellow-100 text-yellow-800'
      case 'interview_scheduled': return 'bg-purple-100 text-purple-800'
      case 'interview_completed': return 'bg-indigo-100 text-indigo-800'
      case 'offer_extended': return 'bg-orange-100 text-orange-800'
      case 'hired': return 'bg-green-100 text-green-800'
      case 'rejected': return 'bg-red-100 text-red-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const getCandidateStatusIcon = (status: string) => {
    switch (status) {
      case 'applied': return <FileText className="w-4 h-4" />
      case 'screening': return <Eye className="w-4 h-4" />
      case 'interview_scheduled': return <Calendar className="w-4 h-4" />
      case 'interview_completed': return <CheckCircle className="w-4 h-4" />
      case 'offer_extended': return <Star className="w-4 h-4" />
      case 'hired': return <CheckCircle className="w-4 h-4" />
      case 'rejected': return <XCircle className="w-4 h-4" />
      default: return <Clock className="w-4 h-4" />
    }
  }

  const tabs = [
    { id: 'members', name: 'Members', icon: UserCheck },
    { id: 'positions', name: 'Positions Shared', icon: Briefcase },
    // { id: 'candidates', name: 'Candidates', icon: FileText }, // Hidden for now, will see this in future
  ] as const

  const handleRemoveMember = async (memberId: number, isInvitation: boolean) => {
    const confirmText = isInvitation ? 'Cancel this invitation?' : 'Remove this team member?'
    if (!window.confirm(confirmText)) return
    try {
      setActionMemberId(memberId)
      const response = await apiClient.removeTeamMember(teamId, memberId)
      if (response.success) {
        await loadMembers()
      } else {
        console.error('Failed to remove member', response)
      }
    } catch (error) {
      console.error('Error removing team member:', error)
    } finally {
      setActionMemberId(null)
    }
  }

  return (
    <div className="bg-white rounded-lg shadow-sm">
      {/* Tab Navigation */}
      <div className="border-b border-gray-200">
        <nav className="flex space-x-8 px-6">
          {tabs.map((tab) => {
            const Icon = tab.icon
            const isActive = activeTab === tab.id
            
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                  isActive
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Icon className="w-4 h-4" />
                <span>{tab.name}</span>
              </button>
            )
          })}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="p-6">
        {loading && activeTab !== 'positions' ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : (
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            {activeTab === 'positions' && (
              <TeamSharedPositions teamId={teamId} />
            )}

            {activeTab === 'members' && (
              <div className="space-y-4">
                {/* Team Info Section */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <h4 className="text-sm font-medium text-gray-500">Type</h4>
                      <p className="text-sm text-gray-900 capitalize">{team.team_type}</p>
                    </div>
                    <div>
                      <h4 className="text-sm font-medium text-gray-500">Members</h4>
                      <p className="text-sm text-gray-900">
                        {team.member_count} / {team.max_members}
                      </p>
                    </div>
                    <div>
                      <h4 className="text-sm font-medium text-gray-500">Status</h4>
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${team.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                        {team.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                    <div>
                      <h4 className="text-sm font-medium text-gray-500">Created</h4>
                      <p className="text-sm text-gray-900">
                        {new Date(team.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  {team.description && (
                    <div className="mt-4">
                      <h4 className="text-sm font-medium text-gray-500">Description</h4>
                      <p className="text-sm text-gray-900 mt-1">{team.description}</p>
                    </div>
                  )}
                </div>

                {/* Team Lead Section */}
                {team.team_lead && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-900 mb-2 flex items-center">
                      <Crown className="w-4 h-4 mr-2 text-yellow-600" />
                      Team Lead
                    </h4>
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                      <div className="flex items-center">
                        <div className="w-8 h-8 bg-yellow-200 rounded-full flex items-center justify-center">
                          <span className="text-yellow-800 text-sm font-medium">
                            {team.team_lead.name.charAt(0)}
                          </span>
                        </div>
                        <div className="ml-3">
                          <p className="text-sm font-medium text-gray-900">
                            {team.team_lead.name}
                          </p>
                          <p className="text-sm text-gray-600">
                            {team.team_lead.email}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Team Members Section */}
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Team Members ({members.length})</h3>
                  
                  {members.length === 0 ? (
                    <div className="text-center py-12">
                      <Users className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                      <h3 className="text-lg font-medium text-gray-900 mb-2">No team members</h3>
                      <p className="text-gray-600">No team members have been added yet</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {members.map((member) => (
                        <motion.div
                          key={member.id}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50"
                        >
                          <div className="flex items-center space-x-4">
                            <div className={`p-2 rounded-full ${
                              member.role === 'lead' ? 'bg-yellow-100' :
                              member.role === 'admin' ? 'bg-blue-100' : 'bg-gray-100'
                            }`}>
                              {getRoleIcon(member.role)}
                            </div>
                            <div>
                              <h4 className="font-medium text-gray-900">{member.name}</h4>
                              <div className="flex items-center space-x-4 text-sm text-gray-500">
                                <span className="flex items-center">
                                  <Mail className="w-3 h-3 mr-1" />
                                  {member.email}
                                </span>
                                {member.joinedAt && (
                                  <span className="flex items-center">
                                    <Calendar className="w-3 h-3 mr-1" />
                                    Joined {new Date(member.joinedAt).toLocaleDateString()}
                                  </span>
                                )}
                              </div>
                              {member.permissions && member.permissions.length > 0 && (
                                <div className="flex items-center space-x-1 mt-1">
                                  <span className="text-xs text-gray-500">Permissions:</span>
                                  {member.permissions.slice(0, 2).map((permission, idx) => (
                                    <span key={idx} className="text-xs bg-gray-100 text-gray-700 px-2 py-0.5 rounded">
                                      {permission}
                                    </span>
                                  ))}
                                  {member.permissions.length > 2 && (
                                    <span className="text-xs text-gray-500">+{member.permissions.length - 2} more</span>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                          
                          <div className="flex items-center space-x-3">
                            <span className={`px-2 py-1 text-xs font-medium rounded-full ${getRoleColor(member.role)}`}>
                              {member.role}
                            </span>
                            <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(member.status)}`}>
            {member.status}
                            </span>
                            {!memberMatchesCurrentUser(member) && (
                              <button
                                disabled={actionMemberId === member.id}
                                onClick={() => handleRemoveMember(member.id as number, member.status === 'pending')}
                                className={`text-red-600 hover:text-red-800 text-xs font-medium ${actionMemberId === member.id ? 'opacity-50 cursor-not-allowed' : ''}`}
                              >
                                {member.status === 'pending' ? 'Cancel Invitation' : 'Remove'}
                              </button>
                            )}
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {activeTab === 'candidates' && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-medium text-gray-900">Assigned Candidates ({candidates.length})</h3>
                  <div className="flex items-center space-x-2">
                    <select className="text-sm border border-gray-300 rounded-md px-3 py-1">
                      <option>All Statuses</option>
                      <option>Applied</option>
                      <option>Screening</option>
                      <option>Interview Scheduled</option>
                      <option>Hired</option>
                      <option>Rejected</option>
                    </select>
                  </div>
                </div>
                
                {candidates.length === 0 ? (
                  <div className="text-center py-12">
                    <FileText className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No candidates assigned</h3>
                    <p className="text-gray-600">Candidates will appear here when they are assigned to this team</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {candidates.map((candidate) => (
                      <motion.div
                        key={candidate.id}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50"
                      >
                        <div className="flex items-center space-x-4">
                          <div className="p-2 bg-blue-100 rounded-full">
                            <FileText className="w-5 h-5 text-blue-600" />
                          </div>
                          <div>
                            <h4 className="font-medium text-gray-900">{candidate.name}</h4>
                            <p className="text-sm text-gray-600">{candidate.position}</p>
                            <div className="flex items-center space-x-4 text-xs text-gray-500 mt-1">
                              <span className="flex items-center">
                                <Mail className="w-3 h-3 mr-1" />
                                {candidate.email}
                              </span>
                              <span className="flex items-center">
                                <Calendar className="w-3 h-3 mr-1" />
                                Assigned {new Date(candidate.assigned_date).toLocaleDateString()}
                              </span>
                              <span>by {candidate.assigned_by}</span>
                            </div>
                            <p className="text-xs text-gray-600 mt-1 font-medium">{candidate.interview_progress}</p>
                          </div>
                        </div>
                        
                        <div className="flex items-center space-x-3">
                          <span className={`flex items-center space-x-1 px-2 py-1 text-xs font-medium rounded-full ${getCandidateStatusColor(candidate.status)}`}>
                            {getCandidateStatusIcon(candidate.status)}
                            <span className="capitalize">{candidate.status.replace('_', ' ')}</span>
                          </span>
                          <div className="flex items-center space-x-1">
                            <button className="text-gray-400 hover:text-blue-600 p-1" title="View Details">
                              <Eye className="w-4 h-4" />
                            </button>
                            <button className="text-gray-400 hover:text-green-600 p-1" title="Send Message">
                              <MessageSquare className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </motion.div>
        )}
      </div>
    </div>
  )
}
